import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import {
  ACCENT_SWATCHES,
  MAX_MESSAGE_LENGTH,
  MENTION_EVERYONE,
  OWNER_ROLE_ID,
  PERMISSIONS,
  QUICK_REACTIONS,
  ROLE_SWATCHES,
  channelKey as makeChannelKey,
  dmPartner,
  formatBytes,
} from "@shared/protocol";
import type { ChannelKind, Message, PermissionId, PresenceStatus, User } from "@shared/types";
import {
  C,
  FONT_MONO,
  FONT_SANS,
  formatShortStamp,
  formatStamp,
  hexA,
  initialOf,
  statusColor,
  statusLabel,
} from "@/theme";
import {
  activeChannel,
  activeDmUser,
  activeVoiceChannel,
  can,
  colorOf,
  connectedVoiceChannel,
  dmEntries,
  mentionsOf,
  messageById,
  messagesOf,
  onlineCount,
  participantsOf,
  pendingFriendRequests,
  roleMembers,
  serverMembers,
  roleOf,
  typingIn,
  unreadOf,
  useApp,
  userById,
} from "@/state/store";
import type { SettingsTab } from "@/state/appTypes";
import { FriendsPanel } from "@/components/FriendsPanel";
import { InstallAppButton } from "@/components/InstallAppButton";
import { plainPreview, renderRichText } from "@/lib/richText";
import {
  ChevronIcon,
  CloseIcon,
  HashIcon,
  HeadphonesIcon,
  MembersIcon,
  MicIcon,
  PowerIcon,
  ScreenIcon,
  SendIcon,
  SettingsIcon,
  SpeakerIcon,
} from "@/components/icons";
import { AttachmentView } from "@/components/AttachmentView";
import { EmojiPicker } from "@/components/EmojiPicker";
import { ProfileEditor } from "@/components/ProfileEditor";
import { ProfileModal } from "@/components/ProfileModal";
import { QuickSwitcher } from "@/components/QuickSwitcher";
import { ShareScreenModal } from "@/components/ShareScreenModal";
import { VideoTile } from "@/components/VideoTile";

/** Home (listas) ou dentro de uma conversa; qual conversa vem do `activeKey`. */
type Screen = "home" | "conv";

/** Para onde vai o emoji escolhido na gaveta: compositor ou reacao de uma mensagem. */
type EmojiTarget = { kind: "composer" } | { kind: "reacao"; messageId: string };

/** Distancia do fim abaixo da qual a lista continua colada no ultimo recado. */
const STICK_THRESHOLD = 120;

/** Distancia do topo (px) que ja pede a pagina anterior do historico. */
const LOAD_OLDER_THRESHOLD = 320;

/** Estados que o proprio membro escolhe na gaveta do rodape. */
const STATUS_OPTIONS: {
  id: Exclude<PresenceStatus, "offline">;
  label: string;
  hint: string;
}[] = [
  { id: "online", label: "Online", hint: "Disponível para a crew" },
  { id: "ausente", label: "Ausente", hint: "Longe do teclado" },
  { id: "ocupado", label: "Ocupado", hint: "Não quero ser avisado agora" },
  { id: "invisivel", label: "Invisível", hint: "Você aparece como offline" },
];

const SETTINGS_TABS: { id: SettingsTab; label: string }[] = [
  { id: "geral", label: "Visão geral" },
  { id: "canais", label: "Canais" },
  { id: "cargos", label: "Cargos" },
  { id: "membros", label: "Membros" },
];

/** O contrato compartilhado guarda as descricoes sem acento; a tela usa as do desenho. */
const PERM_DESC: Partial<Record<PermissionId, string>> = {
  cargos: "Editar cargos e permissões",
};

const LABEL: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".12em",
  color: C.textFaint,
};

const FIELD: CSSProperties = {
  padding: 12,
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 10,
  background: C.field,
  color: C.text,
  fontSize: 15,
  outline: 0,
};

/** Botao de icone pequeno usado nas linhas de canal, cargo e membro. */
const ROW_ICON_BTN: CSSProperties = {
  width: 34,
  height: 34,
  flex: "0 0 auto",
  display: "grid",
  placeItems: "center",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 9,
  cursor: "pointer",
  background: C.raised,
  color: C.textMuted,
};

/** Pilula das acoes de mensagem e da confirmacao de remocao. */
const PILL_BTN: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 10px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 9,
  cursor: "pointer",
  background: C.raised,
  color: C.textSoft,
  fontSize: 12,
  fontWeight: 600,
};

/* -------------------------------------------------------------------- */
/* Glifos que so existem nesta tela                                      */
/* -------------------------------------------------------------------- */

type GlyphProps = { size?: number };

function SearchGlyph({ size = 17 }: GlyphProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
    >
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8 20 20" />
    </svg>
  );
}

function ClipGlyph({ size = 18 }: GlyphProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19.5 11.8 12 19.3a4.6 4.6 0 0 1-6.5-6.5l8-8a3 3 0 0 1 4.3 4.3l-8 8a1.5 1.5 0 0 1-2.1-2.1l7.3-7.3" />
    </svg>
  );
}

function ReplyGlyph({ size = 14 }: GlyphProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.5 6.5 5 11l4.5 4.5" />
      <path d="M5 11h9a5 5 0 0 1 5 5v1.5" />
    </svg>
  );
}

function EmojiGlyph({ size = 14 }: GlyphProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9 10.2h.01 M15 10.2h.01" />
      <path d="M8.4 14.4a4.6 4.6 0 0 0 7.2 0" />
    </svg>
  );
}

function PencilGlyph({ size = 14 }: GlyphProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4.5 19.5h4L19 9a2.5 2.5 0 0 0-3.5-3.5L5 16l-.5 3.5z" />
      <path d="M14.5 6.5 18 10" />
    </svg>
  );
}

function TrashGlyph({ size = 14 }: GlyphProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4.5 7h15" />
      <path d="M9.5 7V4.8h5V7" />
      <path d="M6.8 7 7.8 19.2h8.4L17.2 7" />
      <path d="M10.5 10.5v5.5 M13.5 10.5v5.5" />
    </svg>
  );
}

function PlusGlyph({ size = 15 }: GlyphProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
    >
      <path d="M12 5v14 M5 12h14" />
    </svg>
  );
}

function CheckGlyph({ size = 15 }: GlyphProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12.5 10 17.5 19 7" />
    </svg>
  );
}

/* -------------------------------------------------------------------- */
/* Utilitarios visuais                                                   */
/* -------------------------------------------------------------------- */

/**
 * Tom solido do avatar empilhado na lista de voz: mistura a cor do membro com
 * o fundo escuro, igual ao `mix()` do desenho mobile.
 */
function mixInk(hex: string, amount: number): string {
  const raw = hex.replace("#", "");
  const full = raw.length === 3 ? raw.split("").map(part => part + part).join("") : raw;
  const channel = (slice: string) => Math.round(parseInt(slice, 16) * amount + 20 * (1 - amount));
  return `rgb(${channel(full.slice(0, 2))},${channel(full.slice(2, 4))},${channel(full.slice(4, 6))})`;
}

type AvatarProps = {
  name: string;
  color: string;
  url: string | null;
  size: number;
  fontSize: number;
  style?: CSSProperties;
};

/** Foto real do membro; sem foto, a inicial sobre a cor dele, como no desenho. */
function Avatar({ name, color, url, size, fontSize, style }: AvatarProps): ReactElement {
  const base: CSSProperties = {
    width: size,
    height: size,
    flex: "0 0 auto",
    display: "grid",
    placeItems: "center",
    borderRadius: "50%",
    overflow: "hidden",
    fontSize,
    fontWeight: 600,
    background: hexA(color, 0.18),
    color,
    ...style,
  };
  if (!url) return <span style={base}>{initialOf(name)}</span>;
  return (
    <span style={base}>
      <img
        src={url}
        alt=""
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </span>
  );
}

/** Reproduz uma trilha remota fora da arvore visivel (mic ou audio da tela). */
function RemoteAudio({ stream, muted }: { stream: MediaStream; muted: boolean }) {
  const ref = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || element.srcObject === stream) return;
    element.srcObject = stream;
    if (!muted) void element.play().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    // `pause()` e inequivoco (nao decodifica nada) — `muted` sozinho depende
    // de detalhe interno do navegador sobre a captura de audio do sistema.
    if (muted) {
      element.pause();
    } else {
      void element.play().catch(() => undefined);
    }
  }, [muted]);

  return <audio ref={ref} autoPlay muted={muted} />;
}

/** Trecho curto de uma mensagem, para citacoes e resultados de busca. */
function excerptOf(message: Message): string {
  if (message.deleted) return "mensagem apagada";
  const flat = plainPreview(message.body, 120);
  if (flat) return flat;
  if (message.attachments.length > 0) return message.attachments[0].name;
  return "sem texto";
}

/* -------------------------------------------------------------------- */
/* Tela                                                                  */
/* -------------------------------------------------------------------- */

export function MobileApp() {
  const { state, actions } = useApp();
  const [screen, setScreen] = useState<Screen>("home");
  const [membersSheet, setMembersSheet] = useState(false);
  const [nameDraft, setNameDraft] = useState(state.settings.name);
  const [descDraft, setDescDraft] = useState(state.settings.description);
  const [roleDraft, setRoleDraft] = useState<{ id: string; name: string }>({ id: "", name: "" });
  const [editDraft, setEditDraft] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [actionsFor, setActionsFor] = useState<string | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [iconError, setIconError] = useState<string | null>(null);
  const [newText, setNewText] = useState("");
  const [newVoice, setNewVoice] = useState("");
  const [channelEdit, setChannelEdit] = useState<{
    kind: ChannelKind;
    id: string;
    name: string;
    topic: string;
  } | null>(null);

  const msgsRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const iconRef = useRef<HTMLInputElement | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const jumpPending = useRef<string | null>(null);
  const navKey = useRef(state.activeKey);
  /** Falso quando o usuario subiu para ler o historico: nao arrastamos a vista. */
  const stickRef = useRef(true);
  const scrollKeyRef = useRef("");

  // Abrir uma conversa de fora da lista (perfil, busca, barra de voz) leva para dentro dela.
  useEffect(() => {
    if (navKey.current === state.activeKey) return;
    navKey.current = state.activeKey;
    setScreen("conv");
    setActionsFor(null);
  }, [state.activeKey]);

  const messages = messagesOf(state, state.activeKey);
  const messageCount = messages.length;
  const searchOpen = state.search.open;
  const scrollKey = `${state.activeKey}:${messageCount}`;
  /** Primeira mensagem da lista, para reconhecer a pagina que entrou ACIMA. */
  const firstIdRef = useRef<string | null>(null);
  /** Altura da lista na ultima medida, para saber o quanto ela cresceu. */
  const lastHeightRef = useRef(0);

  // Antes da pintura: a pagina anterior entra acima do que esta na tela, e sem
  // somar de volta a altura que apareceu a leitura salta para tras sozinha.
  useLayoutEffect(() => {
    const element = msgsRef.current;
    if (!element || searchOpen) {
      firstIdRef.current = messages[0]?.id ?? null;
      lastHeightRef.current = 0;
      return;
    }
    const firstId = messages[0]?.id ?? null;
    const mudouConversa = !scrollKeyRef.current.startsWith(`${state.activeKey}:`);
    const anterior = firstIdRef.current;
    firstIdRef.current = firstId;
    const veioAntes =
      !mudouConversa &&
      !stickRef.current &&
      anterior !== null &&
      firstId !== null &&
      firstId !== anterior &&
      messages.some(item => item.id === anterior);
    if (veioAntes) {
      element.scrollTop += element.scrollHeight - lastHeightRef.current;
      // Assumir a chave impede o efeito de baixo de tratar isto como mensagem
      // nova e puxar a vista para o fim.
      scrollKeyRef.current = scrollKey;
    }
    lastHeightRef.current = element.scrollHeight;
  });

  // Na busca e na tela de voz a lista sai do DOM: zerar a chave faz o retorno
  // cair no fim de novo, em vez de reaparecer no topo do historico.
  useEffect(() => {
    if (jumpPending.current) return;
    const element = msgsRef.current;
    if (!element || searchOpen) {
      scrollKeyRef.current = "";
      return;
    }
    if (scrollKeyRef.current === scrollKey) return;
    const changedConversation = !scrollKeyRef.current.startsWith(`${state.activeKey}:`);
    scrollKeyRef.current = scrollKey;
    if (changedConversation) stickRef.current = true;
    if (changedConversation || stickRef.current) element.scrollTop = element.scrollHeight;
    // `highlightId` entra nas dependencias para que a desistencia do salto
    // (mensagem fora do historico carregado) devolva a vista para o fim.
  }, [scrollKey, searchOpen, state.activeKey, screen, highlightId]);

  // Levar ate a mensagem pedida na busca ou na citacao de uma resposta.
  useEffect(() => {
    const pending = jumpPending.current;
    if (!pending || searchOpen) return;
    const element = rowRefs.current.get(pending);
    if (!element) return;
    jumpPending.current = null;
    // Assumir a posicao atual como a corrente impede que o efeito de cima
    // devolva a vista para o fim no proximo recado que chegar.
    stickRef.current = false;
    scrollKeyRef.current = scrollKey;
    element.scrollIntoView({ block: "center" });
  }, [highlightId, scrollKey, searchOpen, screen]);

  const onMessagesScroll = () => {
    const element = msgsRef.current;
    if (!element) return;
    stickRef.current =
      element.scrollHeight - element.scrollTop - element.clientHeight < STICK_THRESHOLD;
    // Perto do topo, pede o que veio antes. Pedido repetido morre no store.
    if (element.scrollTop < LOAD_OLDER_THRESHOLD) actions.loadOlderMessages(state.activeKey);
  };

  useEffect(() => {
    if (!highlightId) return;
    const timer = window.setTimeout(() => {
      // Se a mensagem alvo nunca chegou (fora do historico carregado), soltar a
      // trava para o scroll automatico voltar a funcionar.
      jumpPending.current = null;
      setHighlightId(null);
    }, 2400);
    return () => window.clearTimeout(timer);
  }, [highlightId]);

  const sortedRoles = [...state.roles].sort((a, b) => a.position - b.position);
  const selectedRole = state.roles.find(role => role.id === state.roleSel) ?? state.roles[0] ?? null;
  const selectedRoleId = selectedRole?.id ?? "";
  const selectedRoleName = selectedRole?.name ?? "";
  const settingsName = state.settings.name;
  const settingsDesc = state.settings.description;
  const settingsOpen = state.settingsOpen;

  // Os rascunhos voltam a espelhar o servidor quando ele responde ou a tela reabre.
  useEffect(() => {
    setNameDraft(settingsName);
    setDescDraft(settingsDesc);
  }, [settingsDesc, settingsName, settingsOpen]);

  useEffect(() => {
    setRoleDraft({ id: selectedRoleId, name: selectedRoleName });
  }, [selectedRoleId, selectedRoleName]);

  const editingId = state.editing?.id ?? null;
  const editingBody = state.editing?.body ?? "";

  useEffect(() => {
    setEditDraft(editingBody);
  }, [editingBody, editingId]);

  useEffect(() => {
    if (searchOpen) setSearchDraft(state.search.query);
    // A consulta corrente so entra no campo ao abrir; depois quem manda e o teclado.
  }, [searchOpen]);

  const mentionOptions = useMemo(() => {
    if (mentionQuery === null) return [];
    const list: { handle: string; label: string; user: User | null }[] = [];
    if (can(state, "todos") && MENTION_EVERYONE.startsWith(mentionQuery)) {
      list.push({ handle: MENTION_EVERYONE, label: "Todos os membros", user: null });
    }
    for (const user of serverMembers(state)) {
      const matches =
        user.handle.toLowerCase().startsWith(mentionQuery) ||
        user.name.toLowerCase().startsWith(mentionQuery);
      if (matches) list.push({ handle: user.handle, label: user.name, user });
    }
    return list.slice(0, 6);
  }, [mentionQuery, state]);

  const me = state.me;
  if (!me) return null;

  const accent = state.settings.accent;
  const canManage = can(state, "cargos");
  const canChannels = can(state, "canais");
  const canKick = can(state, "expulsar");
  const canSilence = can(state, "silenciar");
  const canWrite = can(state, "mensagens");
  const dim: CSSProperties = canManage ? {} : { opacity: 0.55 };
  const clickable = canManage ? "pointer" : "not-allowed";
  const pedidosPendentes = pendingFriendRequests(state).length;

  const isVoiceKey = state.activeKey.startsWith("voice:");
  const showHome = screen === "home";
  const showVoice = screen === "conv" && isVoiceKey;
  const showChat = screen === "conv" && !isVoiceKey;

  const chan = activeChannel(state);
  const dmUser = activeDmUser(state);
  const voiceRoom = activeVoiceChannel(state);
  const connectedRoom = connectedVoiceChannel(state);
  const myRole = roleOf(state, me.id);
  const myColor = colorOf(state, me.id);
  const draft = state.drafts[state.activeKey] ?? "";
  const editing = state.editing;
  const composerValue = editing ? editDraft : draft;
  const parts = voiceRoom ? participantsOf(state, voiceRoom.id) : [];
  const audioParts = state.voiceId ? participantsOf(state, state.voiceId) : [];
  // No navegador (sem app desktop) a captura de audio do sistema pega tudo,
  // inclusive a propria call — so da pra evitar o eco ensurdecendo local pra
  // quem esta transmitindo, so enquanto o audio do sistema estiver incluso.
  const webLocalMuteForCapture =
    !Boolean(window.lfrcordDesktop) && state.sharing && state.shareSystemAudio;
  const readyAttachments = state.uploads.filter(upload => upload.attachment !== null).length;
  const typingNames = typingIn(state, state.activeKey);

  const memberGroups = sortedRoles
    .map(role => ({ role, members: serverMembers(state).filter(user => user.roleId === role.id) }))
    .filter(group => group.members.length > 0);

  const conversationLabel = (key: string): string => {
    if (key.startsWith("ch:")) {
      const channel = state.channels.find(item => item.id === key.slice(3));
      return channel ? `#${channel.name}` : "canal removido";
    }
    const partnerId = dmPartner(key, me.id);
    const partner = partnerId ? userById(state, partnerId) : undefined;
    return partner ? `@${partner.handle}` : "direta";
  };

  const openChannel = (channelId: string) => {
    actions.selectChannel(channelId);
    setScreen("conv");
  };

  const openDm = (userId: string) => {
    actions.selectDm(userId);
    setScreen("conv");
  };

  const openVoice = (voiceId: string) => {
    if (state.voiceId === voiceId) {
      actions.selectVoiceView(voiceId);
      setScreen("conv");
      return;
    }
    actions.joinVoice(voiceId);
    // Sem a permissao `voz` a loja so publica o aviso e o `activeKey` nao muda;
    // trocar de tela levaria o usuario para uma conversa que ele nao pediu.
    if (can(state, "voz")) setScreen("conv");
  };

  const leaveVoice = () => {
    // A volta automatica ao texto nao deve arrastar o usuario para a conversa.
    navKey.current = state.lastTextKey;
    actions.leaveVoice();
    setScreen("home");
  };

  const openMember = (userId: string) => {
    setMembersSheet(false);
    actions.openProfile(userId);
  };

  const jumpTo = (message: Message) => {
    setHighlightId(message.id);
    jumpPending.current = message.id;
    actions.jumpToMessage(message);
    setActionsFor(null);
    setScreen("conv");
  };

  const submitComposer = () => {
    if (editing) {
      const text = editDraft.trim();
      if (!text) return;
      actions.submitEdit(text);
      return;
    }
    if (!draft.trim() && readyAttachments === 0) return;
    actions.send();
    setMentionQuery(null);
    setEmojiOpen(false);
  };

  const onComposerChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (editing) setEditDraft(value);
    else {
      actions.setDraft(value);
      actions.notifyTyping();
    }
    const caret = event.target.selectionStart ?? value.length;
    const found = /(^|\s)@([A-Za-z0-9._-]*)$/.exec(value.slice(0, caret));
    setMentionQuery(found ? found[2].toLowerCase() : null);
    setMentionIndex(0);
  };

  const applyMention = (handle: string) => {
    const element = inputRef.current;
    const value = composerValue;
    const caret = element?.selectionStart ?? value.length;
    const before = value.slice(0, caret);
    const found = /(^|\s)@([A-Za-z0-9._-]*)$/.exec(before);
    if (!found) return;
    const start = before.length - found[2].length - 1;
    const next = `${value.slice(0, start)}@${handle} ${value.slice(caret)}`;
    if (editing) setEditDraft(next);
    else actions.setDraft(next);
    setMentionQuery(null);
    const position = start + handle.length + 2;
    window.requestAnimationFrame(() => {
      const field = inputRef.current;
      if (!field) return;
      field.focus();
      field.setSelectionRange(position, position);
    });
  };

  const onComposerKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (mentionQuery !== null && mentionOptions.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setMentionIndex(index => (index + 1) % mentionOptions.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setMentionIndex(index => (index - 1 + mentionOptions.length) % mentionOptions.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        const option = mentionOptions[mentionIndex] ?? mentionOptions[0];
        applyMention(option.handle);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setMentionQuery(null);
        return;
      }
    }
    if (event.key === "Escape" && editing) {
      event.preventDefault();
      actions.startEditing(null);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      submitComposer();
    }
  };

  const onComposerPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (files.length === 0) return;
    event.preventDefault();
    actions.attachFiles(files);
  };

  const onPickFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = "";
    if (files.length > 0) actions.attachFiles(files);
  };

  const uploadIcon = async (file: File) => {
    setIconError(null);
    try {
      const url = await actions.uploadImage(file);
      actions.saveSettings({ iconUrl: url });
    } catch (error) {
      setIconError(
        error instanceof Error ? error.message : "Não foi possível enviar a imagem do ícone.",
      );
    }
  };

  const onPickIcon = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) void uploadIcon(file);
  };

  const saveOverview = () => {
    if (!canManage) return;
    actions.saveSettings({
      name: nameDraft.trim() || state.settings.name,
      description: descDraft.trim(),
      accent: state.settings.accent,
    });
  };

  const commitRoleName = () => {
    if (!canManage || !selectedRole) return;
    const name = roleDraft.name.trim();
    // Campo vazio nao apaga o nome do cargo: volta ao valor que veio do servidor.
    if (!name) {
      setRoleDraft({ id: selectedRole.id, name: selectedRole.name });
      return;
    }
    if (name === selectedRole.name) return;
    actions.updateRole(selectedRole.id, { name });
  };

  const commitChannelEdit = () => {
    if (!channelEdit) return;
    const name = channelEdit.name.trim();
    if (!name) {
      setChannelEdit(null);
      return;
    }
    actions.updateChannel(channelEdit.kind, channelEdit.id, {
      name,
      ...(channelEdit.kind === "text" ? { topic: channelEdit.topic.trim() } : {}),
    });
    setChannelEdit(null);
  };

  const chatTitle = dmUser ? dmUser.name : chan ? `#${chan.name}` : "";
  const chatTopic = dmUser ? statusLabel(dmUser.status) : chan ? chan.topic : "";
  const introTitle = dmUser ? `Conversa com ${dmUser.name}` : `Bem-vindo a #${chan ? chan.name : ""}`;
  const introSub = dmUser
    ? `Mensagens diretas entre você e ${dmUser.name}.`
    : "Este é o começo do canal.";
  const composerPlaceholder = editing
    ? "Editando a mensagem"
    : dmUser
      ? `Mensagem para ${dmUser.name}`
      : `Mensagem em #${chan ? chan.name : ""}`;

  const typingLine =
    typingNames.length === 0
      ? ""
      : typingNames.length === 1
        ? `${typingNames[0]} está digitando”?”`
        : typingNames.length === 2
          ? `${typingNames[0]} e ${typingNames[1]} estão digitando”?”`
          : "Várias pessoas estão digitando”?”";

  const canSend = editing ? editDraft.trim().length > 0 : draft.trim().length > 0 || readyAttachments > 0;

  /* ---------------------------------------------------------------- */
  /* Mensagem                                                          */
  /* ---------------------------------------------------------------- */

  const renderMessage = (message: Message, index: number): ReactElement => {
    const author = userById(state, message.authorId);
    const authorRole = roleOf(state, message.authorId);
    const color = colorOf(state, message.authorId);
    const previous = index > 0 ? messages[index - 1] : null;
    const showHeader =
      previous === null || previous.authorId !== message.authorId || message.replyToId !== null;
    const replied = message.replyToId ? messageById(state, message.replyToId) : undefined;
    const mine = message.authorId === me.id;
    const open = actionsFor === message.id;
    const highlighted = highlightId === message.id;
    const mentionsMe = message.mentions.includes(me.id);

    return (
      <div
        key={message.id}
        ref={element => {
          if (element) rowRefs.current.set(message.id, element);
          else rowRefs.current.delete(message.id);
        }}
        style={{
          padding: "5px 2px",
          borderRadius: 10,
          background: highlighted
            ? "var(--acs,rgba(46,107,255,.14))"
            : mentionsMe && !message.deleted
              ? hexA(accent, 0.07)
              : "transparent",
          transition: "background 240ms ease",
        }}
      >
        {replied ? (
          <button
            onClick={() => jumpTo(replied)}
            title="Ir para a mensagem respondida"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              width: "100%",
              marginLeft: 42,
              marginBottom: 2,
              padding: 0,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span
              style={{
                width: 12,
                height: 7,
                flex: "0 0 auto",
                borderLeft: `1.5px solid ${C.track}`,
                borderTop: `1.5px solid ${C.track}`,
                borderTopLeftRadius: 5,
              }}
            />
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: colorOf(state, replied.authorId),
                flex: "0 0 auto",
              }}
            >
              {userById(state, replied.authorId)?.name ?? "Desconhecido"}
            </span>
            <span
              style={{
                fontSize: 11.5,
                color: C.textFaint,
                minWidth: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {excerptOf(replied)}
            </span>
          </button>
        ) : null}

        {showHeader ? (
          <div
            onClick={() => setActionsFor(open ? null : message.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 8,
              cursor: "pointer",
            }}
          >
            <Avatar
              name={author ? author.name : "?"}
              color={color}
              url={author?.avatarUrl ?? null}
              size={32}
              fontSize={13}
            />
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color:
                  authorRole && authorRole.id === OWNER_ROLE_ID ? "var(--ac,#1F4FD8)" : C.textStrong,
              }}
            >
              {author ? author.name : "Desconhecido"}
            </span>
            <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: C.textLabelDim }}>
              {formatShortStamp(message.createdAt)}
            </span>
          </div>
        ) : null}

        <div
          onClick={() => {
            if (message.deleted) return;
            setActionsFor(open ? null : message.id);
          }}
          style={{
            paddingLeft: 42,
            marginTop: 2,
            fontSize: 14.5,
            lineHeight: 1.5,
            color: message.deleted ? C.textLabelDim : C.textBody,
            fontStyle: message.deleted ? "italic" : "normal",
            overflowWrap: "break-word",
            cursor: message.deleted ? "default" : "pointer",
          }}
        >
          {message.deleted
            ? "Mensagem apagada"
            : renderRichText(message.body, state.users, message.id)}
          {!message.deleted && message.editedAt ? (
            <span style={{ marginLeft: 6, fontSize: 10.5, color: C.textLabelDim }}>(editada)</span>
          ) : null}
        </div>

        {!message.deleted && message.attachments.length > 0 ? (
          <div style={{ display: "grid", gap: 8, margin: "8px 0 4px 42px" }}>
            {message.attachments.map(attachment => (
              <AttachmentView key={attachment.id} attachment={attachment} />
            ))}
          </div>
        ) : null}

        {!message.deleted && message.reactions.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "6px 0 2px 42px" }}>
            {message.reactions.map(reaction => {
              const mineReaction = reaction.userIds.includes(me.id);
              return (
                <button
                  key={reaction.emoji}
                  onClick={() => actions.toggleReaction(message.id, reaction.emoji)}
                  title={`${reaction.userIds.length} membro(s)`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "3px 8px",
                    borderRadius: 999,
                    cursor: "pointer",
                    background: mineReaction ? "var(--acs,rgba(46,107,255,.14))" : C.raised,
                    border: `1px solid ${mineReaction ? "var(--ac,#1F4FD8)" : C.lineStrong}`,
                    color: mineReaction ? "var(--ac,#1F4FD8)" : C.textSoft,
                  }}
                >
                  <span style={{ fontSize: 13 }}>{reaction.emoji}</span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, fontWeight: 600 }}>
                    {reaction.userIds.length}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}

        {state.reactionPickerFor === message.id ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              margin: "6px 0 2px 42px",
              padding: 6,
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 11,
              background: C.raised,
            }}
          >
            {QUICK_REACTIONS.map(emoji => (
              <button
                key={emoji}
                onClick={() => {
                  actions.toggleReaction(message.id, emoji);
                  actions.openReactionPicker(null);
                }}
                className="act-bg-24"
                style={{
                  width: 36,
                  height: 36,
                  display: "grid",
                  placeItems: "center",
                  border: 0,
                  borderRadius: 9,
                  cursor: "pointer",
                  background: "transparent",
                  fontSize: 18,
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        ) : null}

        {open && !message.deleted ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "8px 0 2px 42px" }}>
            {canWrite ? (
              <button
                onClick={() => {
                  actions.setReplyTo(message);
                  setActionsFor(null);
                  inputRef.current?.focus();
                }}
                style={PILL_BTN}
              >
                <ReplyGlyph />
                Responder
              </button>
            ) : null}
            <button
              onClick={() => {
                actions.openReactionPicker(
                  state.reactionPickerFor === message.id ? null : message.id,
                );
              }}
              style={PILL_BTN}
            >
              <EmojiGlyph />
              Reagir
            </button>
            {mine ? (
              <button
                onClick={() => {
                  actions.startEditing(message);
                  setActionsFor(null);
                  inputRef.current?.focus();
                }}
                style={PILL_BTN}
              >
                <PencilGlyph />
                Editar
              </button>
            ) : null}
            {mine || canKick ? (
              confirming === `msg:${message.id}` ? (
                <>
                  <button
                    onClick={() => {
                      actions.deleteMessage(message.id);
                      setConfirming(null);
                      setActionsFor(null);
                    }}
                    style={{
                      ...PILL_BTN,
                      borderColor: C.red,
                      color: C.red,
                      background: hexA(C.red, .12),
                    }}
                  >
                    <CheckGlyph size={14} />
                    Confirmar
                  </button>
                  <button onClick={() => setConfirming(null)} style={PILL_BTN}>
                    Cancelar
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setConfirming(`msg:${message.id}`)}
                  style={{ ...PILL_BTN, color: C.red }}
                >
                  <TrashGlyph />
                  Apagar
                </button>
              )
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */

  return (
    <div
      style={{
        height: "100dvh",
        maxWidth: 520,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        background: C.app,
        color: C.text,
        fontFamily: FONT_SANS,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {showHome ? (
        <>
          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "14px 16px 12px",
              borderBottom: `1px solid ${C.lineRow}`,
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                flex: "0 0 auto",
                display: "grid",
                placeItems: "center",
                borderRadius: 10,
                overflow: "hidden",
                background: state.settings.iconUrl ? C.raised : "var(--ac,#1F4FD8)",
                color: C.ink,
                fontWeight: 700,
                fontSize: 17,
              }}
            >
              {state.settings.iconUrl ? (
                <img
                  src={state.settings.iconUrl}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                state.settings.icon
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: ".05em", lineHeight: 1.1 }}>
                {state.settings.name}
              </div>
              <div
                style={{
                  marginTop: 2,
                  fontFamily: FONT_MONO,
                  fontSize: 9,
                  letterSpacing: ".12em",
                  color: C.textLabel,
                }}
              >
                {`LFR CREW · ${onlineCount(state)} ONLINE`}
              </div>
            </div>
            <button
              onClick={actions.openSettings}
              title="Configurações"
              style={{
                width: 40,
                height: 40,
                display: "grid",
                placeItems: "center",
                border: 0,
                borderRadius: 10,
                cursor: "pointer",
                background: C.raised,
                color: C.textMuted,
              }}
            >
              <SettingsIcon size={17} bg={C.raised} />
            </button>
          </div>

          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              gap: 4,
              margin: "12px 14px 6px",
              padding: 3,
              borderRadius: 11,
              background: C.field,
              border: `1px solid ${C.lineSoft}`,
            }}
          >
            <button
              onClick={() => actions.setTab("canais")}
              style={{
                flex: 1,
                padding: "10px 0",
                border: 0,
                borderRadius: 9,
                cursor: "pointer",
                fontSize: 13.5,
                fontWeight: 600,
                background: state.tab === "canais" ? C.selected : "transparent",
                color: state.tab === "canais" ? C.text : C.textMuted,
              }}
            >
              Canais
            </button>
            <button
              onClick={() => actions.setTab("diretas")}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "10px 0",
                border: 0,
                borderRadius: 9,
                cursor: "pointer",
                fontSize: 13.5,
                fontWeight: 600,
                background: state.tab === "diretas" ? C.selected : "transparent",
                color: state.tab === "diretas" ? C.text : C.textMuted,
              }}
            >
              Mensagens
              {pedidosPendentes > 0 ? (
                <span
                  style={{
                    minWidth: 18,
                    height: 18,
                    display: "grid",
                    placeItems: "center",
                    padding: "0 5px",
                    borderRadius: 999,
                    background: C.red,
                    color: C.text,
                    fontFamily: FONT_MONO,
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {pedidosPendentes}
                </span>
              ) : null}
            </button>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "6px 10px 14px" }}>
            {state.tab === "canais" ? (
              <>
                <div
                  style={{
                    margin: "12px 10px 6px",
                    fontFamily: FONT_MONO,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: ".14em",
                    color: C.textLabelDim,
                  }}
                >
                  TEXTO
                </div>
                {state.channels.map(channel => {
                  const key = makeChannelKey(channel.id);
                  const unread = unreadOf(state, key);
                  const mentions = mentionsOf(state, key);
                  return (
                    <button
                      key={channel.id}
                      onClick={() => openChannel(channel.id)}
                      className="act-bg-1f"
                      style={{
                        width: "100%",
                        minHeight: 48,
                        display: "flex",
                        alignItems: "center",
                        gap: 11,
                        padding: "10px 12px",
                        margin: "1px 0",
                        border: 0,
                        borderRadius: 10,
                        cursor: "pointer",
                        textAlign: "left",
                        background: "transparent",
                        color: unread > 0 ? C.text : C.textSoft,
                      }}
                    >
                      <HashIcon size={17} style={{ opacity: 0.75, flex: "0 0 auto" }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 15, fontWeight: 600 }}>
                          {`#${channel.name}`}
                        </span>
                        <span
                          style={{
                            display: "block",
                            fontSize: 11.5,
                            color: C.textFaint,
                            marginTop: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {channel.topic}
                        </span>
                      </span>
                      {unread > 0 ? (
                        <span
                          style={{
                            fontFamily: FONT_MONO,
                            fontSize: 10.5,
                            fontWeight: 600,
                            padding: "3px 8px",
                            borderRadius: 999,
                            background: "var(--ac,#1F4FD8)",
                            color: C.ink,
                          }}
                        >
                          {/* O arroba marca que ha mencao direta a voce dentro do canal. */}
                          {mentions > 0 ? `@${unread}` : unread}
                        </span>
                      ) : null}
                    </button>
                  );
                })}

                <div
                  style={{
                    margin: "18px 10px 6px",
                    fontFamily: FONT_MONO,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: ".14em",
                    color: C.textLabelDim,
                  }}
                >
                  VOZ
                </div>
                {state.voiceChannels.map(room => {
                  const roomParts = participantsOf(state, room.id);
                  return (
                    <button
                      key={room.id}
                      onClick={() => openVoice(room.id)}
                      className="act-bg-1f"
                      style={{
                        width: "100%",
                        minHeight: 48,
                        display: "flex",
                        alignItems: "center",
                        gap: 11,
                        padding: "10px 12px",
                        margin: "1px 0",
                        border: 0,
                        borderRadius: 10,
                        cursor: "pointer",
                        textAlign: "left",
                        background: "transparent",
                        color: C.textSoft,
                      }}
                    >
                      <SpeakerIcon size={17} style={{ opacity: 0.75, flex: "0 0 auto" }} />
                      <span style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>{room.name}</span>
                      <span style={{ display: "flex", alignItems: "center" }}>
                        {roomParts.map(part => {
                          const user = userById(state, part.userId);
                          const color = colorOf(state, part.userId);
                          if (user?.avatarUrl) {
                            return (
                              <span
                                key={part.peerId}
                                style={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: "50%",
                                  marginLeft: -6,
                                  border: `2px solid ${C.app}`,
                                  overflow: "hidden",
                                  flex: "0 0 auto",
                                }}
                              >
                                <img
                                  src={user.avatarUrl}
                                  alt=""
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                />
                              </span>
                            );
                          }
                          return (
                            <span
                              key={part.peerId}
                              style={{
                                width: 22,
                                height: 22,
                                display: "grid",
                                placeItems: "center",
                                borderRadius: "50%",
                                marginLeft: -6,
                                border: `2px solid ${C.app}`,
                                fontSize: 9.5,
                                fontWeight: 600,
                                background: mixInk(color, 0.35),
                                color,
                              }}
                            >
                              {initialOf(user ? user.name : "?")}
                            </span>
                          );
                        })}
                      </span>
                      {roomParts.length > 0 ? (
                        <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: C.textFaint }}>
                          {roomParts.length}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </>
            ) : null}

            {state.tab === "diretas" ? (
              <>
                {/*
                  Adicionar amigo e responder solicitações no CELULAR.
                  Sem isto, quem usa o telefone recebia o pedido no servidor e
                  não tinha onde ele aparecer — daí a impressão, correta, de
                  que o pedido "não chegava".
                */}
                <FriendsPanel />
                <div
                  style={{
                    margin: "12px 10px 6px",
                    fontFamily: FONT_MONO,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: ".14em",
                    color: C.textLabelDim,
                  }}
                >
                  CONVERSAS
                </div>
                {dmEntries(state).map(entry => {
                  const color = colorOf(state, entry.user.id);
                  const unread = unreadOf(state, entry.key);
                  const preview = entry.last
                    ? `${entry.last.authorId === me.id ? "você: " : ""}${excerptOf(entry.last)}`
                    : "";
                  return (
                    <button
                      key={entry.key}
                      onClick={() => openDm(entry.user.id)}
                      className="act-bg-1f"
                      style={{
                        width: "100%",
                        minHeight: 56,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        margin: "1px 0",
                        border: 0,
                        borderRadius: 10,
                        cursor: "pointer",
                        textAlign: "left",
                        background: "transparent",
                        color: C.textStrong,
                      }}
                    >
                      <span style={{ position: "relative", width: 38, height: 38, flex: "0 0 auto" }}>
                        <Avatar
                          name={entry.user.name}
                          color={color}
                          url={entry.user.avatarUrl}
                          size={38}
                          fontSize={15}
                        />
                        <span
                          style={{
                            position: "absolute",
                            right: -1,
                            bottom: -1,
                            width: 11,
                            height: 11,
                            borderRadius: 999,
                            border: `2px solid ${C.app}`,
                            background: statusColor(entry.user.status),
                          }}
                        />
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 15, fontWeight: 600 }}>{entry.user.name}</span>
                          <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: C.textLabelDim }}>
                            {entry.last ? formatShortStamp(entry.last.createdAt) : ""}
                          </span>
                        </span>
                        <span
                          style={{
                            display: "block",
                            marginTop: 2,
                            fontSize: 12.5,
                            color: C.textDim,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {preview}
                        </span>
                      </span>
                      {unread > 0 ? (
                        <span
                          style={{
                            fontFamily: FONT_MONO,
                            fontSize: 10.5,
                            fontWeight: 600,
                            padding: "3px 8px",
                            borderRadius: 999,
                            background: "var(--ac,#1F4FD8)",
                            color: C.ink,
                          }}
                        >
                          {unread}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </>
            ) : null}
          </div>

          {connectedRoom ? (
            <button
              onClick={() => openVoice(connectedRoom.id)}
              style={{
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                gap: 9,
                margin: "0 14px 8px",
                padding: "10px 12px",
                border: `1px solid ${hexA(C.green, .35)}`,
                borderRadius: 11,
                cursor: "pointer",
                background: hexA(C.green, .08),
                textAlign: "left",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: C.green,
                  animation: "blinkdot 1.4s ease-in-out infinite",
                }}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontFamily: FONT_MONO,
                    fontSize: 9,
                    letterSpacing: ".1em",
                    color: C.green,
                  }}
                >
                  VOZ CONECTADA
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.textBody,
                    marginTop: 1,
                  }}
                >
                  {`${connectedRoom.name} — toque para voltar`}
                </span>
              </span>
              <ChevronIcon size={16} color={C.green} />
            </button>
          ) : null}

          {/*
            Mesmo botão do rodapé do desktop. Aqui ele quase nunca aparece —
            este layout é escolhido por largura de tela, e o instalador é um
            .exe de Windows —, mas uma janela estreita no PC cai neste lado e
            não teria por onde baixar o app.
          */}
          <InstallAppButton />

          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px 14px",
              borderTop: `1px solid ${C.lineRow}`,
              background: C.panelFoot,
            }}
          >
            <button
              onClick={actions.openProfileEditor}
              title="Editar perfil"
              className="act-bg-24"
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "4px 6px 4px 4px",
                border: 0,
                borderRadius: 10,
                cursor: "pointer",
                background: "transparent",
                textAlign: "left",
              }}
            >
              <span style={{ position: "relative", width: 36, height: 36, flex: "0 0 auto" }}>
                <Avatar
                  name={me.name}
                  color={myColor}
                  url={me.avatarUrl}
                  size={36}
                  fontSize={14}
                />
                <span
                  style={{
                    position: "absolute",
                    right: -1,
                    bottom: -1,
                    width: 11,
                    height: 11,
                    borderRadius: 999,
                    border: `2px solid ${C.panelFoot}`,
                    background: statusColor(me.status),
                  }}
                />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1.15,
                    color: C.text,
                  }}
                >
                  {me.name}
                </span>
                <span
                  style={{
                    display: "block",
                    fontFamily: FONT_MONO,
                    fontSize: 9.5,
                    color: C.textLabel,
                    marginTop: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {`@${me.handle} · ${myRole ? myRole.name.toLowerCase() : ""}`}
                </span>
              </span>
            </button>
            <button
              onClick={actions.toggleMute}
              title={me.serverMuted ? "Silenciado por um moderador" : "Microfone"}
              style={{
                width: 40,
                height: 40,
                flex: "0 0 auto",
                display: "grid",
                placeItems: "center",
                border: 0,
                borderRadius: 10,
                cursor: "pointer",
                background: state.muted || me.serverMuted ? hexA(C.red, .16) : C.raised,
                color: state.muted || me.serverMuted ? C.red : C.textMuted,
              }}
            >
              <MicIcon size={17} off={state.muted || me.serverMuted} />
            </button>
            <button
              onClick={actions.toggleDeafen}
              title="Áudio"
              style={{
                width: 40,
                height: 40,
                flex: "0 0 auto",
                display: "grid",
                placeItems: "center",
                border: 0,
                borderRadius: 10,
                cursor: "pointer",
                background: state.deafened ? hexA(C.red, .16) : C.raised,
                color: state.deafened ? C.red : C.textMuted,
              }}
            >
              <HeadphonesIcon size={17} off={state.deafened} />
            </button>
          </div>
        </>
      ) : null}

      {showChat ? (
        <>
          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 10px",
              borderBottom: `1px solid ${C.lineRow}`,
            }}
          >
            <button
              onClick={() => setScreen("home")}
              title="Voltar"
              className="act-bg-24"
              style={{
                width: 40,
                height: 40,
                display: "grid",
                placeItems: "center",
                border: 0,
                borderRadius: 10,
                cursor: "pointer",
                background: "transparent",
                color: C.textSoft,
              }}
            >
              <ChevronIcon size={18} dir="left" />
            </button>
            {dmUser ? (
              <Avatar
                name={dmUser.name}
                color={colorOf(state, dmUser.id)}
                url={dmUser.avatarUrl}
                size={28}
                fontSize={11.5}
              />
            ) : null}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 700, lineHeight: 1.15 }}>{chatTitle}</div>
              <div
                style={{
                  fontSize: 11,
                  color: C.textFaint,
                  marginTop: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {chatTopic}
              </div>
            </div>
            <button
              onClick={() => (searchOpen ? actions.closeSearch() : actions.openSearch())}
              title="Buscar mensagens"
              className="act-bg-24"
              style={{
                width: 40,
                height: 40,
                flex: "0 0 auto",
                display: "grid",
                placeItems: "center",
                border: 0,
                borderRadius: 10,
                cursor: "pointer",
                background: searchOpen ? C.hoverStrong : "transparent",
                color: searchOpen ? "var(--ac,#1F4FD8)" : C.textMuted,
              }}
            >
              <SearchGlyph size={17} />
            </button>
            {dmUser ? null : (
              <button
                onClick={() => setMembersSheet(true)}
                title="Membros"
                className="act-bg-24"
                style={{
                  width: 40,
                  height: 40,
                  flex: "0 0 auto",
                  display: "grid",
                  placeItems: "center",
                  border: 0,
                  borderRadius: 10,
                  cursor: "pointer",
                  background: "transparent",
                  color: C.textMuted,
                }}
              >
                <MembersIcon size={17} />
              </button>
            )}
          </div>

          {searchOpen ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
              <div style={{ flex: "0 0 auto", padding: "10px 12px 6px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "5px 5px 5px 12px",
                    border: `1px solid ${C.lineStrong}`,
                    borderRadius: 13,
                    background: C.raised,
                  }}
                >
                  <SearchGlyph size={16} />
                  <input
                    value={searchDraft}
                    autoFocus
                    onChange={event => setSearchDraft(event.target.value)}
                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        actions.closeSearch();
                        return;
                      }
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      const query = searchDraft.trim();
                      if (query) actions.runSearch(query);
                    }}
                    placeholder="Buscar nas mensagens"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      border: 0,
                      outline: 0,
                      background: "transparent",
                      color: C.text,
                      fontSize: 15,
                      padding: "9px 0",
                    }}
                  />
                  <button
                    onClick={actions.closeSearch}
                    title="Fechar busca"
                    className="act-bg-24"
                    style={{
                      width: 36,
                      height: 36,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      border: 0,
                      borderRadius: 10,
                      cursor: "pointer",
                      background: "transparent",
                      color: C.textMuted,
                    }}
                  >
                    <CloseIcon size={14} />
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: "auto", padding: "4px 12px 14px" }}>
                {state.search.loading ? (
                  <div
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: 10.5,
                      letterSpacing: ".12em",
                      color: C.textLabel,
                      padding: "12px 4px",
                    }}
                  >
                    BUSCANDO”?”
                  </div>
                ) : null}
                {!state.search.loading && state.search.query && state.search.results.length === 0 ? (
                  <div style={{ padding: "12px 4px", fontSize: 13, color: C.textFaint }}>
                    Nada encontrado para ”?o{state.search.query}”?”.
                  </div>
                ) : null}
                {state.search.results.map(result => {
                  const author = userById(state, result.authorId);
                  const color = colorOf(state, result.authorId);
                  return (
                    <button
                      key={result.id}
                      onClick={() => jumpTo(result)}
                      className="act-bg-1f"
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        padding: "10px 10px",
                        margin: "2px 0",
                        border: 0,
                        borderRadius: 10,
                        cursor: "pointer",
                        textAlign: "left",
                        background: "transparent",
                      }}
                    >
                      <Avatar
                        name={author ? author.name : "?"}
                        color={color}
                        url={author?.avatarUrl ?? null}
                        size={30}
                        fontSize={12.5}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span style={{ fontSize: 13.5, fontWeight: 600, color: C.textStrong }}>
                            {author ? author.name : "Desconhecido"}
                          </span>
                          <span
                            style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: C.textLabelDim }}
                          >
                            {`${conversationLabel(result.channelKey)} · ${formatStamp(result.createdAt)}`}
                          </span>
                        </span>
                        <span
                          style={{
                            display: "block",
                            marginTop: 2,
                            fontSize: 13,
                            color: C.textSoft,
                            overflowWrap: "break-word",
                          }}
                        >
                          {excerptOf(result)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div
              ref={msgsRef}
              onScroll={onMessagesScroll}
              style={{ flex: 1, overflowY: "auto", padding: "14px 14px 6px" }}
            >
              <div
                style={{
                  padding: "4px 2px 16px",
                  borderBottom: `1px solid ${C.lineSoft}`,
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 700 }}>{introTitle}</div>
                <div style={{ marginTop: 4, fontSize: 12.5, color: C.textDim }}>{introSub}</div>
              </div>
              {messages.map(renderMessage)}
            </div>
          )}

          {searchOpen ? null : (
            <div style={{ flex: "0 0 auto", padding: "8px 12px 14px" }}>
              {typingLine ? (
                <div
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: 10,
                    letterSpacing: ".06em",
                    color: C.textLabel,
                    padding: "0 4px 6px",
                  }}
                >
                  {typingLine}
                </div>
              ) : null}

              {state.replyTo ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                    padding: "8px 8px 8px 12px",
                    border: `1px solid ${C.lineStrong}`,
                    borderLeft: "3px solid var(--ac,#1F4FD8)",
                    borderRadius: 10,
                    background: C.raised,
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontFamily: FONT_MONO,
                        fontSize: 9.5,
                        letterSpacing: ".1em",
                        color: "var(--ac,#1F4FD8)",
                      }}
                    >
                      {`RESPONDENDO A ${(
                        userById(state, state.replyTo.authorId)?.name ?? "Desconhecido"
                      ).toUpperCase()}`}
                    </span>
                    <span
                      style={{
                        display: "block",
                        marginTop: 2,
                        fontSize: 12.5,
                        color: C.textDim,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {excerptOf(state.replyTo)}
                    </span>
                  </span>
                  <button
                    onClick={() => actions.setReplyTo(null)}
                    title="Cancelar resposta"
                    className="act-bg-24"
                    style={{
                      width: 32,
                      height: 32,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      border: 0,
                      borderRadius: 9,
                      cursor: "pointer",
                      background: "transparent",
                      color: C.textMuted,
                    }}
                  >
                    <CloseIcon size={13} />
                  </button>
                </div>
              ) : null}

              {editing ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                    padding: "8px 8px 8px 12px",
                    border: `1px solid ${C.lineStrong}`,
                    borderRadius: 10,
                    background: C.raised,
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: FONT_MONO,
                      fontSize: 9.5,
                      letterSpacing: ".1em",
                      color: C.textFaint,
                    }}
                  >
                    EDITANDO A MENSAGEM · ESC CANCELA
                  </span>
                  <button
                    onClick={() => actions.startEditing(null)}
                    title="Cancelar edição"
                    className="act-bg-24"
                    style={{
                      width: 32,
                      height: 32,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      border: 0,
                      borderRadius: 9,
                      cursor: "pointer",
                      background: "transparent",
                      color: C.textMuted,
                    }}
                  >
                    <CloseIcon size={13} />
                  </button>
                </div>
              ) : null}

              {state.uploads.length > 0 ? (
                <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
                  {state.uploads.map(upload => (
                    <div
                      key={upload.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 8px 8px 12px",
                        border: `1px solid ${upload.error ? C.red : C.lineStrong}`,
                        borderRadius: 10,
                        background: C.raised,
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: "block",
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: C.textStrong,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {upload.name}
                        </span>
                        <span
                          style={{
                            display: "block",
                            marginTop: 2,
                            fontFamily: FONT_MONO,
                            fontSize: 10,
                            color: upload.error ? C.red : C.textFaint,
                          }}
                        >
                          {upload.error
                            ? upload.error
                            : upload.attachment
                              ? `PRONTO · ${formatBytes(upload.size)}`
                              : `${Math.round(upload.progress * 100)}% · ${formatBytes(upload.size)}`}
                        </span>
                        {!upload.attachment && !upload.error ? (
                          <span
                            style={{
                              display: "block",
                              height: 3,
                              marginTop: 6,
                              borderRadius: 999,
                              background: C.lineStrong,
                              overflow: "hidden",
                            }}
                          >
                            <span
                              style={{
                                display: "block",
                                height: "100%",
                                width: `${Math.round(upload.progress * 100)}%`,
                                background: "var(--ac,#1F4FD8)",
                                transition: "width 160ms linear",
                              }}
                            />
                          </span>
                        ) : null}
                      </span>
                      <button
                        onClick={() => actions.removeUpload(upload.id)}
                        title="Remover anexo"
                        className="act-bg-24"
                        style={{
                          width: 32,
                          height: 32,
                          flex: "0 0 auto",
                          display: "grid",
                          placeItems: "center",
                          border: 0,
                          borderRadius: 9,
                          cursor: "pointer",
                          background: "transparent",
                          color: C.textMuted,
                        }}
                      >
                        <CloseIcon size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              {mentionQuery !== null && mentionOptions.length > 0 ? (
                <div
                  style={{
                    marginBottom: 8,
                    padding: 4,
                    border: `1px solid ${C.lineStrong}`,
                    borderRadius: 11,
                    background: C.raised,
                    maxHeight: 220,
                    overflowY: "auto",
                  }}
                >
                  {mentionOptions.map((option, index) => (
                    <button
                      key={option.handle}
                      onClick={() => applyMention(option.handle)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 10px",
                        border: 0,
                        borderRadius: 9,
                        cursor: "pointer",
                        textAlign: "left",
                        background: index === mentionIndex ? C.selected : "transparent",
                      }}
                    >
                      {option.user ? (
                        <Avatar
                          name={option.user.name}
                          color={colorOf(state, option.user.id)}
                          url={option.user.avatarUrl}
                          size={26}
                          fontSize={11}
                        />
                      ) : (
                        <span
                          style={{
                            width: 26,
                            height: 26,
                            flex: "0 0 auto",
                            display: "grid",
                            placeItems: "center",
                            borderRadius: "50%",
                            background: "var(--acs,rgba(46,107,255,.14))",
                            color: "var(--ac,#1F4FD8)",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          @
                        </span>
                      )}
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: "block",
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: C.textStrong,
                          }}
                        >
                          {option.label}
                        </span>
                        <span
                          style={{
                            display: "block",
                            fontFamily: FONT_MONO,
                            fontSize: 10,
                            color: C.textLabel,
                            marginTop: 1,
                          }}
                        >
                          {`@${option.handle}`}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}

              {emojiOpen ? (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginBottom: 8,
                    padding: 6,
                    border: `1px solid ${C.lineStrong}`,
                    borderRadius: 11,
                    background: C.raised,
                  }}
                >
                  {QUICK_REACTIONS.map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => {
                        const next = `${composerValue}${emoji}`;
                        if (editing) setEditDraft(next);
                        else actions.setDraft(next);
                        setEmojiOpen(false);
                        inputRef.current?.focus();
                      }}
                      className="act-bg-24"
                      style={{
                        width: 38,
                        height: 38,
                        display: "grid",
                        placeItems: "center",
                        border: 0,
                        borderRadius: 9,
                        cursor: "pointer",
                        background: "transparent",
                        fontSize: 19,
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              ) : null}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "5px 5px 5px 8px",
                  border: `1px solid ${C.lineStrong}`,
                  borderRadius: 13,
                  background: C.raised,
                }}
              >
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  onChange={onPickFiles}
                  style={{ display: "none" }}
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  title="Anexar arquivo"
                  disabled={!canWrite}
                  className="act-bg-24"
                  style={{
                    width: 38,
                    height: 38,
                    flex: "0 0 auto",
                    display: "grid",
                    placeItems: "center",
                    border: 0,
                    borderRadius: 10,
                    cursor: canWrite ? "pointer" : "not-allowed",
                    background: "transparent",
                    color: C.textMuted,
                    opacity: canWrite ? 1 : 0.5,
                  }}
                >
                  <ClipGlyph size={18} />
                </button>
                <input
                  ref={inputRef}
                  value={composerValue}
                  maxLength={MAX_MESSAGE_LENGTH}
                  onChange={onComposerChange}
                  onKeyDown={onComposerKey}
                  onPaste={onComposerPaste}
                  placeholder={canWrite ? composerPlaceholder : "Você não pode escrever aqui"}
                  readOnly={!canWrite}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 0,
                    outline: 0,
                    background: "transparent",
                    color: C.text,
                    fontSize: 15,
                    padding: "9px 4px",
                  }}
                />
                <button
                  onClick={() => setEmojiOpen(open => !open)}
                  title="Emoji"
                  className="act-bg-24"
                  style={{
                    width: 38,
                    height: 38,
                    flex: "0 0 auto",
                    display: "grid",
                    placeItems: "center",
                    border: 0,
                    borderRadius: 10,
                    cursor: "pointer",
                    background: "transparent",
                    color: emojiOpen ? "var(--ac,#1F4FD8)" : C.textMuted,
                  }}
                >
                  <EmojiGlyph size={18} />
                </button>
                <button
                  onClick={submitComposer}
                  title={editing ? "Salvar edição" : "Enviar"}
                  disabled={!canSend}
                  className="act-press"
                  style={{
                    width: 40,
                    height: 40,
                    flex: "0 0 auto",
                    display: "grid",
                    placeItems: "center",
                    border: 0,
                    borderRadius: 10,
                    cursor: canSend ? "pointer" : "not-allowed",
                    background: "var(--ac,#1F4FD8)",
                    color: C.ink,
                    opacity: canSend ? 1 : 0.45,
                  }}
                >
                  {editing ? <CheckGlyph size={17} /> : <SendIcon size={17} />}
                </button>
              </div>
            </div>
          )}
        </>
      ) : null}

      {showVoice ? (
        <>
          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 10px",
              borderBottom: `1px solid ${C.lineRow}`,
            }}
          >
            <button
              onClick={() => setScreen("home")}
              title="Voltar"
              className="act-bg-24"
              style={{
                width: 40,
                height: 40,
                display: "grid",
                placeItems: "center",
                border: 0,
                borderRadius: 10,
                cursor: "pointer",
                background: "transparent",
                color: C.textSoft,
              }}
            >
              <ChevronIcon size={18} dir="left" />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 700, lineHeight: 1.15 }}>
                {voiceRoom ? voiceRoom.name : ""}
              </div>
              <div style={{ fontFamily: FONT_MONO, fontSize: 10, color: C.green, marginTop: 1 }}>
                {`${parts.length} CONECTADOS`}
              </div>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "16px 14px" }}>
            {parts
              .filter(part => part.sharing)
              .map(part => {
                const isSelf = part.peerId === state.peerId;
                const user = userById(state, part.userId);
                const name = user ? user.name : "Desconhecido";
                const stream = isSelf ? state.local.screen : state.remote[part.peerId]?.screen ?? null;
                return (
                  <div
                    key={`share-${part.peerId}`}
                    style={{
                      aspectRatio: "16 / 9",
                      position: "relative",
                      marginBottom: 10,
                      borderRadius: 14,
                      border: "1px solid var(--ac,#1F4FD8)",
                      background:
                        `repeating-linear-gradient(45deg,${C.hoverStrong} 0 12px,${C.raised} 12px 24px)`,
                      display: "grid",
                      placeItems: "center",
                      overflow: "hidden",
                    }}
                  >
                    {stream ? (
                      <div style={{ position: "absolute", inset: 0 }}>
                        {/* `focused` faz o tile preencher a moldura 16/9 desta tela. */}
                        <VideoTile
                          stream={stream}
                          label={`Tela de ${name}`}
                          kind="screen"
                          live
                          muted
                          focused
                          stats={isSelf ? undefined : state.stats[part.peerId]?.screen}
                        />
                      </div>
                    ) : (
                      <>
                        <span
                          style={{
                            fontFamily: FONT_MONO,
                            fontSize: 10,
                            letterSpacing: ".12em",
                            color: C.textFaint,
                          }}
                        >
                          {`TELA DE ${name.toUpperCase()}`}
                        </span>
                        <span
                          style={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            fontFamily: FONT_MONO,
                            fontSize: 9,
                            fontWeight: 600,
                            letterSpacing: ".1em",
                            padding: "3px 8px",
                            borderRadius: 6,
                            background: "var(--ac,#1F4FD8)",
                            color: C.ink,
                          }}
                        >
                          AO VIVO
                        </span>
                      </>
                    )}
                  </div>
                );
              })}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {parts.map(part => {
                const isSelf = part.peerId === state.peerId;
                const user = userById(state, part.userId);
                const color = colorOf(state, part.userId);
                const name = isSelf ? "você" : user ? user.name : "Desconhecido";
                const speaking =
                  !(state.deafened || webLocalMuteForCapture) &&
                  !part.muted &&
                  (state.speaking[part.peerId] ?? part.speaking);
                const silenced = user?.serverMuted ?? false;
                return (
                  <div
                    key={part.peerId}
                    style={{
                      aspectRatio: "4 / 3.4",
                      position: "relative",
                      borderRadius: 14,
                      border: `1px solid ${C.lineTile}`,
                      background: C.tile,
                      display: "grid",
                      placeItems: "center",
                      animation: "pop 220ms ease",
                    }}
                  >
                    <span
                      style={{
                        width: 54,
                        height: 54,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "50%",
                        overflow: "hidden",
                        background: hexA(color, 0.18),
                        animation: speaking ? "spk 1.6s ease-in-out infinite" : "none",
                      }}
                    >
                      {user?.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : null}
                    </span>
                    {user?.avatarUrl ? null : (
                      <span
                        style={{
                          position: "absolute",
                          width: 54,
                          height: 54,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: "50%",
                          fontSize: 21,
                          fontWeight: 700,
                          color,
                        }}
                      >
                        {initialOf(user ? user.name : "?")}
                      </span>
                    )}
                    {silenced ? (
                      <span
                        style={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          fontFamily: FONT_MONO,
                          fontSize: 8.5,
                          fontWeight: 600,
                          letterSpacing: ".1em",
                          padding: "3px 7px",
                          borderRadius: 6,
                          background: hexA(C.red, .16),
                          color: C.red,
                        }}
                      >
                        SILENCIADO
                      </span>
                    ) : null}
                    <span
                      style={{
                        position: "absolute",
                        left: 8,
                        bottom: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "3px 8px",
                        borderRadius: 7,
                        background: "rgba(0,0,0,.5)",
                        fontFamily: FONT_MONO,
                        fontSize: 10,
                        color: C.textStrong,
                      }}
                    >
                      {name}
                      {part.muted || silenced ? (
                        <MicIcon size={10} color={C.red} bare off />
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              justifyContent: "center",
              padding: "10px 14px 18px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                padding: 7,
                border: `1px solid ${C.lineStrong}`,
                borderRadius: 999,
                background: C.raised,
              }}
            >
              <button
                onClick={actions.toggleMute}
                title={me.serverMuted ? "Silenciado por um moderador" : "Microfone"}
                style={{
                  width: 46,
                  height: 46,
                  display: "grid",
                  placeItems: "center",
                  border: 0,
                  borderRadius: 999,
                  cursor: "pointer",
                  background: state.muted || me.serverMuted ? hexA(C.red, .16) : C.lineTile,
                  color: state.muted || me.serverMuted ? C.red : C.textStrong,
                }}
              >
                <MicIcon size={19} off={state.muted || me.serverMuted} />
              </button>
              <button
                onClick={actions.toggleDeafen}
                title="Áudio"
                style={{
                  width: 46,
                  height: 46,
                  display: "grid",
                  placeItems: "center",
                  border: 0,
                  borderRadius: 999,
                  cursor: "pointer",
                  background: state.deafened ? hexA(C.red, .16) : C.lineTile,
                  color: state.deafened ? C.red : C.textStrong,
                }}
              >
                <HeadphonesIcon size={19} off={state.deafened} />
              </button>
              <button
                onClick={actions.toggleShare}
                title="Transmitir tela"
                style={{
                  width: 46,
                  height: 46,
                  display: "grid",
                  placeItems: "center",
                  border: 0,
                  borderRadius: 999,
                  cursor: "pointer",
                  background: state.sharing ? hexA(accent, 0.2) : C.lineTile,
                  color: state.sharing ? "var(--ac,#1F4FD8)" : C.textStrong,
                }}
              >
                <ScreenIcon size={19} />
              </button>
              <span style={{ width: 1, height: 26, background: C.lineStrong }} />
              <button
                onClick={leaveVoice}
                style={{
                  height: 46,
                  padding: "0 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  border: 0,
                  borderRadius: 999,
                  cursor: "pointer",
                  background: C.red,
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                <PowerIcon size={16} />
                Sair
              </button>
            </div>
          </div>
        </>
      ) : null}

      {membersSheet ? (
        <div
          onClick={() => setMembersSheet(false)}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 40,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            background: "rgba(0,0,0,.55)",
            animation: "fadein 150ms ease",
          }}
        >
          <div
            onClick={event => event.stopPropagation()}
            style={{
              maxHeight: "72%",
              display: "flex",
              flexDirection: "column",
              borderRadius: "18px 18px 0 0",
              border: `1px solid ${C.lineStrong}`,
              borderBottom: 0,
              background: C.raised,
              animation: "slideup 220ms ease",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 18px 10px",
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 700 }}>
                {`Membros — ${state.users.length}`}
              </span>
              <button
                onClick={() => setMembersSheet(false)}
                title="Fechar"
                className="act-bg-24"
                style={{
                  width: 34,
                  height: 34,
                  display: "grid",
                  placeItems: "center",
                  border: `1px solid ${C.lineStrong}`,
                  borderRadius: 999,
                  cursor: "pointer",
                  background: "transparent",
                  color: C.textMuted,
                }}
              >
                <CloseIcon size={14} />
              </button>
            </div>
            <div style={{ overflowY: "auto", padding: "0 12px 18px" }}>
              {memberGroups.map(group => (
                <div key={group.role.id} style={{ marginTop: 8 }}>
                  <div
                    style={{
                      margin: "4px 8px 6px",
                      fontFamily: FONT_MONO,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: ".14em",
                      color: C.textLabelDim,
                    }}
                  >
                    {`${group.role.name.toUpperCase()} — ${group.members.length}`}
                  </div>
                  {group.members.map(member => {
                    const color = colorOf(state, member.id);
                    return (
                      <button
                        key={member.id}
                        onClick={() => openMember(member.id)}
                        className="act-bg-24"
                        style={{
                          width: "100%",
                          minHeight: 52,
                          display: "flex",
                          alignItems: "center",
                          gap: 11,
                          padding: "8px 10px",
                          border: 0,
                          borderRadius: 10,
                          cursor: "pointer",
                          textAlign: "left",
                          background: "transparent",
                        }}
                      >
                        <span
                          style={{ position: "relative", width: 34, height: 34, flex: "0 0 auto" }}
                        >
                          <Avatar
                            name={member.name}
                            color={color}
                            url={member.avatarUrl}
                            size={34}
                            fontSize={13.5}
                          />
                          <span
                            style={{
                              position: "absolute",
                              right: -1,
                              bottom: -1,
                              width: 10,
                              height: 10,
                              borderRadius: 999,
                              border: `2px solid ${C.raised}`,
                              background: statusColor(member.status),
                            }}
                          />
                        </span>
                        <span style={{ minWidth: 0 }}>
                          <span
                            style={{
                              display: "block",
                              fontSize: 14.5,
                              fontWeight: 600,
                              color:
                                group.role.id === OWNER_ROLE_ID ? "var(--ac,#1F4FD8)" : C.textStrong,
                            }}
                          >
                            {member.name}
                          </span>
                          <span
                            style={{
                              display: "block",
                              fontFamily: FONT_MONO,
                              fontSize: 10,
                              color: C.textLabel,
                              marginTop: 1,
                            }}
                          >
                            {`@${member.handle}`}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* O audio da sala precisa continuar tocando fora da tela de voz. */}
      <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
        {audioParts
          .filter(part => part.peerId !== state.peerId)
          .flatMap(part => {
            const media = state.remote[part.peerId];
            const tracks: ReactElement[] = [];
            const muted = state.deafened || webLocalMuteForCapture;
            if (media?.mic) {
              tracks.push(<RemoteAudio key={`mic-${part.peerId}`} stream={media.mic} muted={muted} />);
            }
            if (media?.screen) {
              tracks.push(
                <RemoteAudio key={`screen-${part.peerId}`} stream={media.screen} muted={muted} />,
              );
            }
            return tracks;
          })}
      </div>

      {state.settingsOpen ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 60,
            display: "flex",
            flexDirection: "column",
            background: C.app,
            animation: "fadein 160ms ease",
          }}
        >
          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 10px",
              borderBottom: `1px solid ${C.lineRow}`,
            }}
          >
            <button
              onClick={actions.closeSettings}
              title="Fechar"
              className="act-bg-24"
              style={{
                width: 40,
                height: 40,
                display: "grid",
                placeItems: "center",
                border: 0,
                borderRadius: 10,
                cursor: "pointer",
                background: "transparent",
                color: C.textSoft,
              }}
            >
              <CloseIcon size={16} />
            </button>
            <span style={{ fontSize: 16, fontWeight: 700 }}>Configurações</span>
          </div>

          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              gap: 4,
              margin: "12px 14px 4px",
              padding: 3,
              borderRadius: 11,
              background: C.field,
              border: `1px solid ${C.lineSoft}`,
            }}
          >
            {SETTINGS_TABS.map(tab => {
              const active = state.settingsTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => actions.setSettingsTab(tab.id)}
                  style={{
                    flex: 1,
                    padding: "9px 0",
                    border: 0,
                    borderRadius: 9,
                    cursor: "pointer",
                    fontSize: 12.5,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    background: active ? C.selected : "transparent",
                    color: active ? C.text : C.textMuted,
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
            {state.settingsTab === "geral" ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                  <input
                    ref={iconRef}
                    type="file"
                    accept="image/*"
                    onChange={onPickIcon}
                    style={{ display: "none" }}
                  />
                  <button
                    onClick={() => {
                      if (canManage) iconRef.current?.click();
                    }}
                    title="Trocar o ícone da comunidade"
                    disabled={!canManage}
                    style={{
                      width: 58,
                      height: 58,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      padding: 0,
                      border: 0,
                      borderRadius: 15,
                      overflow: "hidden",
                      cursor: clickable,
                      background: state.settings.iconUrl ? C.raised : "var(--ac,#1F4FD8)",
                      color: C.ink,
                      fontSize: 26,
                      fontWeight: 700,
                      ...dim,
                    }}
                  >
                    {state.settings.iconUrl ? (
                      <img
                        src={state.settings.iconUrl}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      state.settings.icon
                    )}
                  </button>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: 10,
                        lineHeight: 1.6,
                        color: C.textLabel,
                      }}
                    >
                      ÍCONE DA COMUNIDADE
                      <br />
                      TOQUE PARA ENVIAR UMA IMAGEM
                    </div>
                    {state.settings.iconUrl && canManage ? (
                      <button
                        onClick={() => actions.saveSettings({ iconUrl: null })}
                        style={{
                          marginTop: 7,
                          padding: "6px 10px",
                          border: `1px solid ${C.lineStrong}`,
                          borderRadius: 9,
                          cursor: "pointer",
                          background: "transparent",
                          color: C.textMuted,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Remover imagem
                      </button>
                    ) : null}
                    {iconError ? (
                      <div style={{ marginTop: 6, fontSize: 11.5, color: C.red }}>
                        {iconError}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div style={{ display: "grid", gap: 14 }}>
                  <label style={{ display: "grid", gap: 7 }}>
                    <span style={LABEL}>NOME DA COMUNIDADE</span>
                    <input
                      value={nameDraft}
                      onChange={event => setNameDraft(event.target.value)}
                      readOnly={!canManage}
                      style={{ ...FIELD, ...dim }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 7 }}>
                    <span style={LABEL}>DESCRI”?”fO</span>
                    <textarea
                      rows={3}
                      value={descDraft}
                      onChange={event => setDescDraft(event.target.value)}
                      readOnly={!canManage}
                      style={{ ...FIELD, resize: "vertical", fontFamily: FONT_SANS, ...dim }}
                    />
                  </label>

                  <div>
                    <div style={{ ...LABEL, marginBottom: 8 }}>COR DE ACENTO</div>
                    <div style={{ display: "flex", gap: 9 }}>
                      {ACCENT_SWATCHES.map(color => (
                        <button
                          key={color}
                          title={color}
                          disabled={!canManage}
                          onClick={() => actions.updateSettings({ accent: color })}
                          style={{
                            width: 30,
                            height: 30,
                            padding: 0,
                            borderRadius: 9,
                            cursor: clickable,
                            background: color,
                            border: `2px solid ${color === accent ? C.text : "transparent"}`,
                            ...dim,
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button
                      onClick={saveOverview}
                      disabled={!canManage}
                      style={{
                        padding: "12px 18px",
                        border: 0,
                        borderRadius: 10,
                        cursor: clickable,
                        background: "var(--ac,#1F4FD8)",
                        color: C.ink,
                        fontSize: 14,
                        fontWeight: 700,
                        ...dim,
                      }}
                    >
                      Salvar alterações
                    </button>
                    {state.saved ? (
                      <span
                        style={{
                          fontFamily: FONT_MONO,
                          fontSize: 10.5,
                          color: C.green,
                          animation: "fadein 200ms ease",
                        }}
                      >
                        SALVO
                      </span>
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}

            {state.settingsTab === "canais" ? (
              canChannels ? (
                <>
                  <div style={{ ...LABEL, marginBottom: 8 }}>CANAIS DE TEXTO</div>
                  {state.channels.map(channel => {
                    const editingThis =
                      channelEdit !== null &&
                      channelEdit.kind === "text" &&
                      channelEdit.id === channel.id;
                    if (editingThis && channelEdit) {
                      return (
                        <div
                          key={channel.id}
                          style={{
                            display: "grid",
                            gap: 8,
                            padding: "12px 2px",
                            borderBottom: `1px solid ${C.lineRow}`,
                          }}
                        >
                          <input
                            value={channelEdit.name}
                            autoFocus
                            onChange={event =>
                              setChannelEdit({ ...channelEdit, name: event.target.value })
                            }
                            placeholder="nome-do-canal"
                            style={{ ...FIELD, padding: "10px 12px" }}
                          />
                          <input
                            value={channelEdit.topic}
                            onChange={event =>
                              setChannelEdit({ ...channelEdit, topic: event.target.value })
                            }
                            placeholder="Assunto do canal"
                            style={{ ...FIELD, padding: "10px 12px", fontSize: 14 }}
                          />
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={commitChannelEdit} style={PILL_BTN}>
                              <CheckGlyph size={14} />
                              Salvar
                            </button>
                            <button onClick={() => setChannelEdit(null)} style={PILL_BTN}>
                              Cancelar
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={channel.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "12px 2px",
                          borderBottom: `1px solid ${C.lineRow}`,
                        }}
                      >
                        <HashIcon size={16} style={{ opacity: 0.7, flex: "0 0 auto" }} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            style={{
                              display: "block",
                              fontSize: 14.5,
                              fontWeight: 600,
                              color: C.textStrong,
                            }}
                          >
                            {`#${channel.name}`}
                          </span>
                          <span
                            style={{
                              display: "block",
                              fontSize: 11.5,
                              color: C.textFaint,
                              marginTop: 1,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {channel.topic || "Sem assunto"}
                          </span>
                        </span>
                        {confirming === `ch:${channel.id}` ? (
                          <>
                            <button
                              onClick={() => {
                                actions.deleteChannel("text", channel.id);
                                setConfirming(null);
                              }}
                              style={{
                                ...PILL_BTN,
                                borderColor: C.red,
                                color: C.red,
                                background: hexA(C.red, .12),
                              }}
                            >
                              Apagar mesmo
                            </button>
                            <button onClick={() => setConfirming(null)} style={PILL_BTN}>
                              Não
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                setChannelEdit({
                                  kind: "text",
                                  id: channel.id,
                                  name: channel.name,
                                  topic: channel.topic,
                                })
                              }
                              title="Renomear"
                              style={ROW_ICON_BTN}
                            >
                              <PencilGlyph size={15} />
                            </button>
                            <button
                              onClick={() => setConfirming(`ch:${channel.id}`)}
                              title={
                                state.channels.length <= 1
                                  ? "A comunidade precisa de pelo menos um canal de texto"
                                  : "Apagar canal"
                              }
                              disabled={state.channels.length <= 1}
                              style={{
                                ...ROW_ICON_BTN,
                                color: C.red,
                                opacity: state.channels.length <= 1 ? 0.45 : 1,
                                cursor: state.channels.length <= 1 ? "not-allowed" : "pointer",
                              }}
                            >
                              <TrashGlyph size={15} />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ display: "flex", gap: 8, margin: "12px 0 22px" }}>
                    <input
                      value={newText}
                      onChange={event => setNewText(event.target.value)}
                      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                        if (event.key !== "Enter") return;
                        event.preventDefault();
                        if (!newText.trim()) return;
                        actions.createChannel("text", newText.trim());
                        setNewText("");
                      }}
                      placeholder="novo-canal"
                      style={{ ...FIELD, flex: 1, minWidth: 0, padding: "10px 12px" }}
                    />
                    <button
                      onClick={() => {
                        if (!newText.trim()) return;
                        actions.createChannel("text", newText.trim());
                        setNewText("");
                      }}
                      title="Criar canal de texto"
                      style={{
                        ...ROW_ICON_BTN,
                        width: 42,
                        height: 42,
                        color: "var(--ac,#1F4FD8)",
                      }}
                    >
                      <PlusGlyph size={16} />
                    </button>
                  </div>

                  <div style={{ ...LABEL, marginBottom: 8 }}>SALAS DE VOZ</div>
                  {state.voiceChannels.map(room => {
                    const editingThis =
                      channelEdit !== null &&
                      channelEdit.kind === "voice" &&
                      channelEdit.id === room.id;
                    if (editingThis && channelEdit) {
                      return (
                        <div
                          key={room.id}
                          style={{
                            display: "grid",
                            gap: 8,
                            padding: "12px 2px",
                            borderBottom: `1px solid ${C.lineRow}`,
                          }}
                        >
                          <input
                            value={channelEdit.name}
                            autoFocus
                            onChange={event =>
                              setChannelEdit({ ...channelEdit, name: event.target.value })
                            }
                            placeholder="Nome da sala"
                            style={{ ...FIELD, padding: "10px 12px" }}
                          />
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={commitChannelEdit} style={PILL_BTN}>
                              <CheckGlyph size={14} />
                              Salvar
                            </button>
                            <button onClick={() => setChannelEdit(null)} style={PILL_BTN}>
                              Cancelar
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={room.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "12px 2px",
                          borderBottom: `1px solid ${C.lineRow}`,
                        }}
                      >
                        <SpeakerIcon size={16} style={{ opacity: 0.7, flex: "0 0 auto" }} />
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 14.5,
                            fontWeight: 600,
                            color: C.textStrong,
                          }}
                        >
                          {room.name}
                        </span>
                        {confirming === `vc:${room.id}` ? (
                          <>
                            <button
                              onClick={() => {
                                actions.deleteChannel("voice", room.id);
                                setConfirming(null);
                              }}
                              style={{
                                ...PILL_BTN,
                                borderColor: C.red,
                                color: C.red,
                                background: hexA(C.red, .12),
                              }}
                            >
                              Apagar mesmo
                            </button>
                            <button onClick={() => setConfirming(null)} style={PILL_BTN}>
                              Não
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                setChannelEdit({
                                  kind: "voice",
                                  id: room.id,
                                  name: room.name,
                                  topic: "",
                                })
                              }
                              title="Renomear"
                              style={ROW_ICON_BTN}
                            >
                              <PencilGlyph size={15} />
                            </button>
                            <button
                              onClick={() => setConfirming(`vc:${room.id}`)}
                              title="Apagar sala"
                              style={{ ...ROW_ICON_BTN, color: C.red }}
                            >
                              <TrashGlyph size={15} />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ display: "flex", gap: 8, margin: "12px 0 8px" }}>
                    <input
                      value={newVoice}
                      onChange={event => setNewVoice(event.target.value)}
                      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                        if (event.key !== "Enter") return;
                        event.preventDefault();
                        if (!newVoice.trim()) return;
                        actions.createChannel("voice", newVoice.trim());
                        setNewVoice("");
                      }}
                      placeholder="Nova sala"
                      style={{ ...FIELD, flex: 1, minWidth: 0, padding: "10px 12px" }}
                    />
                    <button
                      onClick={() => {
                        if (!newVoice.trim()) return;
                        actions.createChannel("voice", newVoice.trim());
                        setNewVoice("");
                      }}
                      title="Criar sala de voz"
                      style={{
                        ...ROW_ICON_BTN,
                        width: 42,
                        height: 42,
                        color: "var(--ac,#1F4FD8)",
                      }}
                    >
                      <PlusGlyph size={16} />
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 13.5, color: C.textFaint, padding: "12px 2px" }}>
                  Você não tem permissão para gerenciar canais.
                </div>
              )
            ) : null}

            {state.settingsTab === "cargos" ? (
              <>
                <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 12 }}>
                  {sortedRoles.map(role => (
                    <button
                      key={role.id}
                      onClick={() => actions.selectRole(role.id)}
                      style={{
                        flex: "0 0 auto",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "9px 13px",
                        border: `1px solid ${C.lineStrong}`,
                        borderRadius: 999,
                        cursor: "pointer",
                        background: role.id === selectedRoleId ? C.selected : "transparent",
                        color: C.textStrong,
                        fontSize: 13,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{ width: 8, height: 8, borderRadius: 3, background: role.color }}
                      />
                      {role.name}
                    </button>
                  ))}
                  <button
                    onClick={actions.addRole}
                    disabled={!canManage}
                    className="hov-border"
                    style={{
                      flex: "0 0 auto",
                      padding: "9px 13px",
                      border: `1px dashed ${C.track}`,
                      borderRadius: 999,
                      cursor: clickable,
                      background: "transparent",
                      color: C.textMuted,
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      ...dim,
                    }}
                  >
                    + Novo
                  </button>
                </div>

                {selectedRole ? (
                  <>
                    <label style={{ display: "grid", gap: 7, marginBottom: 14 }}>
                      <span style={LABEL}>NOME DO CARGO</span>
                      <input
                        value={roleDraft.id === selectedRole.id ? roleDraft.name : selectedRole.name}
                        onChange={event =>
                          setRoleDraft({ id: selectedRole.id, name: event.target.value })
                        }
                        onBlur={commitRoleName}
                        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                          if (event.key !== "Enter") return;
                          event.preventDefault();
                          commitRoleName();
                        }}
                        readOnly={!canManage}
                        style={{ ...FIELD, padding: "11px 12px", ...dim }}
                      />
                    </label>

                    <div style={{ marginBottom: 16 }}>
                      <div style={{ ...LABEL, marginBottom: 8 }}>COR DO CARGO</div>
                      <div style={{ display: "flex", gap: 9 }}>
                        {ROLE_SWATCHES.map(color => (
                          <button
                            key={color}
                            title={color}
                            disabled={!canManage}
                            onClick={() => actions.updateRole(selectedRole.id, { color })}
                            style={{
                              width: 30,
                              height: 30,
                              padding: 0,
                              borderRadius: 9,
                              cursor: clickable,
                              background: color,
                              border: `2px solid ${
                                color === selectedRole.color ? C.text : "transparent"
                              }`,
                              ...dim,
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    <div style={{ ...LABEL, marginBottom: 4 }}>PERMISS”.ES</div>
                    {PERMISSIONS.map(perm => {
                      const on = selectedRole.perms[perm.id];
                      return (
                        <div
                          key={perm.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "12px 2px",
                            borderBottom: `1px solid ${C.lineRow}`,
                          }}
                        >
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span
                              style={{
                                display: "block",
                                fontSize: 14,
                                fontWeight: 600,
                                color: C.textStrong,
                              }}
                            >
                              {perm.name}
                            </span>
                            <span
                              style={{
                                display: "block",
                                fontSize: 11.5,
                                color: C.textFaint,
                                marginTop: 1,
                              }}
                            >
                              {PERM_DESC[perm.id] ?? perm.desc}
                            </span>
                          </span>
                          <button
                            disabled={!canManage}
                            aria-pressed={on}
                            title={perm.name}
                            onClick={() => actions.togglePermission(selectedRole.id, perm.id)}
                            style={{
                              width: 44,
                              height: 24,
                              flex: "0 0 auto",
                              position: "relative",
                              padding: 0,
                              border: 0,
                              borderRadius: 999,
                              cursor: clickable,
                              background: on ? "var(--ac,#1F4FD8)" : C.track,
                              transition: "background 150ms ease",
                              ...dim,
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                top: 3,
                                width: 18,
                                height: 18,
                                borderRadius: 999,
                                background: C.text,
                                transition: "left 150ms ease",
                                left: on ? 23 : 3,
                              }}
                            />
                          </button>
                        </div>
                      );
                    })}

                    <div style={{ ...LABEL, margin: "18px 0 8px" }}>MEMBROS COM ESTE CARGO</div>
                    {roleMembers(state, selectedRole.id).length === 0 ? (
                      <div style={{ fontSize: 13, color: C.textFaint, paddingBottom: 8 }}>
                        Nenhum membro tem este cargo.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingBottom: 8 }}>
                        {roleMembers(state, selectedRole.id).map(member => (
                          <span
                            key={member.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              padding: "5px 10px 5px 5px",
                              border: `1px solid ${C.lineStrong}`,
                              borderRadius: 999,
                              background: C.raised,
                              fontSize: 12.5,
                              color: C.textBody,
                            }}
                          >
                            <Avatar
                              name={member.name}
                              color={colorOf(state, member.id)}
                              url={member.avatarUrl}
                              size={22}
                              fontSize={10}
                            />
                            {member.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {canManage && selectedRole.id !== OWNER_ROLE_ID ? (
                      <div style={{ display: "flex", gap: 8, margin: "14px 0 8px" }}>
                        {confirming === `role:${selectedRole.id}` ? (
                          <>
                            <button
                              onClick={() => {
                                actions.deleteRole(selectedRole.id);
                                setConfirming(null);
                              }}
                              style={{
                                ...PILL_BTN,
                                borderColor: C.red,
                                color: C.red,
                                background: hexA(C.red, .12),
                              }}
                            >
                              Apagar mesmo
                            </button>
                            <button onClick={() => setConfirming(null)} style={PILL_BTN}>
                              Não
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setConfirming(`role:${selectedRole.id}`)}
                            style={{ ...PILL_BTN, color: C.red }}
                          >
                            <TrashGlyph />
                            Apagar cargo
                          </button>
                        )}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}

            {state.settingsTab === "membros"
              ? serverMembers(state).map(user => {
                  const color = colorOf(state, user.id);
                  const owner = user.roleId === OWNER_ROLE_ID;
                  const self = user.id === me.id;
                  return (
                    <div
                      key={user.id}
                      style={{
                        display: "grid",
                        gap: 10,
                        padding: "12px 2px",
                        borderBottom: `1px solid ${C.lineRow}`,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                        <Avatar
                          name={user.name}
                          color={color}
                          url={user.avatarUrl}
                          size={36}
                          fontSize={14}
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 14.5, fontWeight: 600 }}>
                            {user.name}
                          </span>
                          <span
                            style={{
                              display: "block",
                              fontFamily: FONT_MONO,
                              fontSize: 10,
                              color: C.textLabel,
                              marginTop: 1,
                            }}
                          >
                            {`@${user.handle}${user.serverMuted ? " · silenciado" : ""}`}
                          </span>
                        </span>
                        <select
                          value={user.roleId}
                          disabled={!canManage || owner}
                          onChange={event => actions.setMemberRole(user.id, event.target.value)}
                          style={{
                            padding: "9px 10px",
                            border: `1px solid ${C.lineStrong}`,
                            borderRadius: 9,
                            background: C.field,
                            color: C.textStrong,
                            fontSize: 13,
                            outline: 0,
                            cursor: canManage && !owner ? "pointer" : "not-allowed",
                            opacity: canManage && !owner ? 1 : 0.55,
                          }}
                        >
                          {sortedRoles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {canSilence || canKick ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {canSilence ? (
                            confirming === `mute:${user.id}` ? (
                              <>
                                <button
                                  onClick={() => {
                                    actions.setServerMute(user.id, !user.serverMuted);
                                    setConfirming(null);
                                  }}
                                  style={{
                                    ...PILL_BTN,
                                    borderColor: "var(--ac,#1F4FD8)",
                                    color: "var(--ac,#1F4FD8)",
                                    background: "var(--acs,rgba(46,107,255,.14))",
                                  }}
                                >
                                  {user.serverMuted ? "Reativar mesmo" : "Silenciar mesmo"}
                                </button>
                                <button onClick={() => setConfirming(null)} style={PILL_BTN}>
                                  Não
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setConfirming(`mute:${user.id}`)}
                                disabled={owner}
                                style={{
                                  ...PILL_BTN,
                                  color: user.serverMuted ? "var(--ac,#1F4FD8)" : C.textSoft,
                                  borderColor: user.serverMuted ? "var(--ac,#1F4FD8)" : C.lineStrong,
                                  cursor: owner ? "not-allowed" : "pointer",
                                  opacity: owner ? 0.5 : 1,
                                }}
                              >
                                <MicIcon
                                  size={14}
                                  off={!user.serverMuted}
                                  cutColor="currentColor"
                                />
                                {user.serverMuted ? "Reativar voz" : "Silenciar"}
                              </button>
                            )
                          ) : null}
                          {canKick ? (
                            confirming === `kick:${user.id}` ? (
                              <>
                                <button
                                  onClick={() => {
                                    actions.kickMember(user.id);
                                    setConfirming(null);
                                  }}
                                  style={{
                                    ...PILL_BTN,
                                    borderColor: C.red,
                                    color: C.red,
                                    background: hexA(C.red, .12),
                                  }}
                                >
                                  Expulsar mesmo
                                </button>
                                <button onClick={() => setConfirming(null)} style={PILL_BTN}>
                                  Não
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setConfirming(`kick:${user.id}`)}
                                disabled={owner || self}
                                style={{
                                  ...PILL_BTN,
                                  color: C.red,
                                  cursor: owner || self ? "not-allowed" : "pointer",
                                  opacity: owner || self ? 0.5 : 1,
                                }}
                              >
                                <TrashGlyph />
                                Expulsar
                              </button>
                            )
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })
              : null}
          </div>
        </div>
      ) : null}

      <ProfileModal />
      <ProfileEditor />
      <ShareScreenModal />
    </div>
  );
}
